import React from 'react';
import { ExternalLink, Calendar, Tag, MapPin, Link as LinkIcon, BarChart2, LineChart, PieChart } from 'lucide-react';
import { LinkedInAd } from '../types/dataTypes';

interface LinkedInAdsDisplayProps {
  ads: LinkedInAd[];
}

const LinkedInAdsDisplay: React.FC<LinkedInAdsDisplayProps> = ({ ads }) => {
  if (!ads || ads.length === 0) {
    return (
      <div className="bg-yellow-50 dark:bg-yellow-900/30 border-l-4 border-yellow-400 p-4 rounded">
        <p className="text-yellow-700 dark:text-yellow-300">
          No LinkedIn ads found.
        </p>
      </div>
    );
  }

  return (
    <div id="linkedin-ads" className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mt-8">
      <div className="relative bg-gradient-to-br from-blue-600 to-blue-800 rounded-lg shadow-xl overflow-hidden p-6 mb-8">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-4 left-4 text-white">
            <BarChart2 size={120} />
          </div>
          <div className="absolute bottom-4 right-4 text-white">
            <LineChart size={120} />
          </div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-white">
            <PieChart size={160} />
          </div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,_rgba(255,255,255,0.2)_0%,_transparent_100%)]"></div>
        </div>
        <div className="relative z-10">
          <h2 className="text-xl font-semibold text-white mb-2">
            LinkedIn Ad Intelligence
          </h2>
          <p className="text-blue-100">
            Monitor your competitors' LinkedIn ad campaigns. Track creative strategies, messaging patterns, and targeting approaches to stay ahead of the competition.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
          🎯 Your Competitors LinkedIn Ads
        </h2>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {ads.length} ads found
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
        {ads.map((ad, index) => (
          <div
            key={ad.id || index}
            className="bg-gray-50 dark:bg-gray-700/50 rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-200"
          >
            {/* Ad Preview Image */}
            {ad.imageURL && (
              <div className="relative group">
                <img
                  src={ad.imageURL}
                  alt={ad.headline || 'Ad preview'}
                  className="w-full h-48 object-cover"
                />
                <a
                  href={ad.imageURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                >
                  <ExternalLink className="text-white" size={24} />
                </a>
              </div>
            )}

            {/* Ad Content */}
            <div className="p-6 space-y-4">
              {/* Format Badge - Moved to top */}
              {ad.format && (
                <div className="mb-4">
                  <div className="flex items-center">
                    <div className="h-4 w-1 bg-purple-500 rounded-full mr-2"></div>
                    <span className="text-xs font-medium text-purple-600 dark:text-purple-400 uppercase tracking-wider">
                      Ad Format
                    </span>
                  </div>
                  <div className="mt-2 flex items-center">
                    <Tag className="w-4 h-4 text-purple-500 mr-2" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {ad.format}
                    </span>
                  </div>
                </div>
              )}

              {/* Headline */}
              {ad.headline && (
                <div className="space-y-2">
                  <div className="flex items-center">
                    <div className="h-4 w-1 bg-blue-500 rounded-full mr-2"></div>
                    <span className="text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wider">
                      Ad Headline
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white leading-snug">
                    {ad.headline}
                  </h3>
                </div>
              )}

              {/* Body Text */}
              <div className="space-y-2">
                <div className="flex items-center">
                  <div className="h-4 w-1 bg-green-500 rounded-full mr-2"></div>
                  <span className="text-xs font-medium text-green-600 dark:text-green-400 uppercase tracking-wider">
                    Ad Copy
                  </span>
                </div>
                <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                  {ad.body}
                </p>
              </div>

              {/* Metadata */}
              <div className="space-y-4 pt-4 mt-4 border-t border-gray-200 dark:border-gray-600">
                {/* CTA */}
                {ad.ctas && (
                  <div className="flex items-center">
                    <div className="h-4 w-1 bg-orange-500 rounded-full mr-2"></div>
                    <span className="text-xs font-medium text-orange-600 dark:text-orange-400 uppercase tracking-wider">
                      Call to Action
                    </span>
                    <div className="ml-auto">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300 border border-orange-100 dark:border-orange-800">
                        {ad.ctas}
                      </span>
                    </div>
                  </div>
                )}

                {/* Location */}
                {ad.targeting_location && (
                  <div className="flex items-center">
                    <div className="h-4 w-1 bg-indigo-500 rounded-full mr-2"></div>
                    <span className="text-xs font-medium text-indigo-600 dark:text-indigo-400 uppercase tracking-wider">
                      Target Region
                    </span>
                    <div className="ml-auto flex items-center">
                      <MapPin className="w-3.5 h-3.5 text-indigo-500 mr-1" />
                      <span className="text-xs text-gray-600 dark:text-gray-300">
                        {ad.targeting_location}
                      </span>
                    </div>
                  </div>
                )}

                {/* Date */}
                {ad.created_at && (
                  <div className="flex items-center">
                    <div className="h-4 w-1 bg-teal-500 rounded-full mr-2"></div>
                    <span className="text-xs font-medium text-teal-600 dark:text-teal-400 uppercase tracking-wider">
                      Published
                    </span>
                    <div className="ml-auto flex items-center">
                      <Calendar className="w-3.5 h-3.5 text-teal-500 mr-1" />
                      <span className="text-xs text-gray-600 dark:text-gray-300">
                        {new Date(ad.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                )}

                {/* Landing Page */}
                {ad.landing_page && (
                  <div className="flex items-center">
                    <div className="h-4 w-1 bg-pink-500 rounded-full mr-2"></div>
                    <span className="text-xs font-medium text-pink-600 dark:text-pink-400 uppercase tracking-wider">
                      Landing Page
                    </span>
                    <div className="ml-auto">
                      <a
                        href={ad.landing_page}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-xs text-pink-600 dark:text-pink-400 hover:underline"
                      >
                        <LinkIcon className="w-3.5 h-3.5 mr-1" />
                        {new URL(ad.landing_page).hostname}
                      </a>
                    </div>
                  </div>
                )}
              </div>

              {/* Company Info */}
              <div className="pt-4 mt-4 border-t border-gray-200 dark:border-gray-600">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">
                    {ad.advertiserName}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LinkedInAdsDisplay;